import { Typography } from "@mui/material";
import { useContext, useState, useEffect } from "react";
import TextEditorWithJSONViewer from "../../../TextEditor/TextEditorWithJSONViewer";
import FontSizeControl from "../../../Common/FontSizeControl";
import useStore from "../../../../Store/Store";
import { theme } from "../../../../Themes/Themes";

import "./SimulationDescription.css";
import { SimulationsContext } from "./SimulationsTab";

export const SimulationDescription: React.FC = () => {
  const {
    currentUnsavedSimulationObject,
    isEditingSimulations,
    setIsEditingSimulations,
    setCurrentUnsavedSimulationObject,
  } = useStore((state) => ({
    currentUnsavedSimulationObject: state.currentUnsavedSimulationObject,
    isEditingSimulations: state.simulationsState.isEditingSimulations,
    setIsEditingSimulations: state.setIsEditingSimulations,
    setCurrentUnsavedSimulationObject: state.setCurrentUnsavedSimulationObject,
  }));

  const { isMaxSD, setSimulationDescription, simulationDescription } =
    useContext(SimulationsContext);

  // Add a state to track if the editor should be rendered (similar to TextEditor pattern)
  const [shouldRenderEditor, setShouldRenderEditor] = useState(false);
  const [editorFontSize, setEditorFontSize] = useState(115); // Default 115% (15% larger)

  // Use an effect to delay editor rendering when simulation changes
  useEffect(() => {
    setShouldRenderEditor(false);
    const timer = setTimeout(() => {
      setShouldRenderEditor(true);
    }, 150); // Delay to ensure DOM is ready after simulation updates

    return () => clearTimeout(timer);
  }, [currentUnsavedSimulationObject.name, isEditingSimulations]); // Reset when simulation changes or editing state changes

  const handleSimulationDescriptionChange = async (data) => {
    setSimulationDescription(data);
    await setCurrentUnsavedSimulationObject({
      ...useStore.getState().currentUnsavedSimulationObject,
      description: data,
    });
  };

  const handleFontSizeChange = (fontSize: number) => {
    setEditorFontSize(fontSize);
  };

  // Helper function to get content with keywords appended for AI-generated simulations
  const getDescriptionWithKeywords = (
    description: string,
    keywords?: string,
    isEditing = false
  ) => {
    if (
      !currentUnsavedSimulationObject.isAIGenerated ||
      !keywords ||
      isEditing
    ) {
      return description;
    }

    const keywordsLine = `\n\n<strong>Keywords:</strong> <em style="color: ${theme.custom.lighterPink};">${keywords}</em>`;
    return description + keywordsLine;
  };

  return (
    <>
      {isEditingSimulations === "Description" && (
        <div className="simulationDescriptionEditContainer">
          <div className="text-editor-container">
            {shouldRenderEditor ? (
              <TextEditorWithJSONViewer
                content={
                  simulationDescription ||
                  currentUnsavedSimulationObject.description ||
                  ""
                }
                disabled={false}
                showToolbar={isEditingSimulations === "Description"}
                handleWikiChange={handleSimulationDescriptionChange}
                isMaxSD={isMaxSD}
                customStyle={{
                  fontSize: `${editorFontSize}%`,
                }}
                enableMarkdown={currentUnsavedSimulationObject.isAIGenerated}
              />
            ) : (
              <div
                className="editor-loading"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "#999",
                  fontSize: "14px",
                }}
              >
                Loading editor...
              </div>
            )}
          </div>
        </div>
      )}

      {isEditingSimulations !== "Description" && (
        <div className="simulationDescriptionPreviewContainer">
          {!currentUnsavedSimulationObject.description && (
            <div
              className="largeGreyButton"
              style={{ width: "95%" }}
              onClick={async (e) => {
                await setIsEditingSimulations("Description");
              }}
            >
              <Typography variant="h6" fontWeight={"thin"}>
                + Add Simulation Overview
              </Typography>
            </div>
          )}
          {currentUnsavedSimulationObject.description && (
            <div className="simulationDescriptionInnerContainer">
              <Typography
                variant="subtitle1"
                color="primary"
                className="boxTitle"
                style={{ zIndex: 99999 }}
              >
                Simulation Overview
              </Typography>

              {/* Font Size Control - positioned at the end of the grey bar */}
              <div
                style={{
                  position: "absolute",
                  top: "-17px",
                  right: "20px",
                  zIndex: 1,
                }}
              >
                <FontSizeControl
                  onFontSizeChange={handleFontSizeChange}
                  disabled={isEditingSimulations === "Description"}
                />
              </div>

              <div className="wikiDiv" style={{ height: "calc(100%)" }}>
                <div className="textEditorContainer">
                  {shouldRenderEditor ? (
                    <TextEditorWithJSONViewer
                      content={getDescriptionWithKeywords(
                        currentUnsavedSimulationObject.description,
                        currentUnsavedSimulationObject.keywords
                      )}
                      disabled={true}
                      showToolbar={false}
                      handleWikiChange={handleSimulationDescriptionChange}
                      isMaxSD={isMaxSD}
                      customStyle={{
                        fontSize: `${editorFontSize}%`,
                      }}
                      enableMarkdown={
                        currentUnsavedSimulationObject.isAIGenerated
                      }
                    />
                  ) : (
                    <div
                      className="editor-loading"
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "#999",
                        fontSize: "14px",
                      }}
                    >
                      Loading editor...
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};
